import React, { Suspense, lazy, useEffect, ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/useAuthStore";
import { AuthInitializer } from "@/components/AuthInitializer";
import { useMovingAverages } from "@/hooks/useMovingAverages";
import { useAutoAdjust } from "@/hooks/useAutoAdjust";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { I18nProvider } from "@/components/I18nProvider";
import { PerformanceMonitor } from "@/components/debug/PerformanceMonitor";
import { CacheMonitor } from "@/components/debug/CacheMonitor";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { PWAUpdatePrompt } from "@/components/pwa/PWAUpdatePrompt";
import { clearAllCaches } from "@/utils/versionCheck";
import { initGlobalErrorMonitoring, reportChunkError } from "@/utils/errorReporter";
import { DataPrefetchProvider } from "@/components/DataPrefetchProvider";
import { PrefetchLoadingIndicator } from "@/components/PrefetchLoadingIndicator";

// ✅ FASE 8: Code Splitting - Páginas críticas carregadas imediatamente
import Landing from "./pages/Landing";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

// ✅ Lazy loading com retry automático para chunks
function lazyRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  retries = 3
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    for (let i = 0; i < retries; i++) {
      try {
        return await componentImport();
      } catch (error) {
        console.warn(`❌ Falha ao carregar chunk (tentativa ${i + 1}/${retries})`);
        
        if (i === retries - 1) {
          // Última tentativa - limpar cache e recarregar
          console.error('🔄 Limpando cache e recarregando...');
          await clearAllCaches();
          window.location.reload();
          throw error;
        }
        
        // Aguardar antes de tentar novamente
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
    throw new Error('Failed to load component');
  });
}

// ✅ FASE 8: Lazy loading de páginas pesadas para reduzir bundle inicial
const OnboardingIntro = lazyRetry(() => import("./pages/onboarding/Intro"));
const OnboardingIncomplete = lazyRetry(() => import("./components/onboarding/OnboardingIncomplete"));
const RodaDaVida = lazyRetry(() => import("./pages/onboarding/RodaDaVida"));
const EscolhaPilares = lazyRetry(() => import("./pages/onboarding/EscolhaPilares"));
const IdentidadeAspiracional = lazyRetry(() => import("./pages/onboarding/IdentidadeAspiracional"));
const Rotina = lazyRetry(() => import("./pages/onboarding/Rotina"));
const DadosPessoais = lazyRetry(() => import("./pages/onboarding/DadosPessoais"));
const Compromisso = lazyRetry(() => import("./pages/onboarding/Compromisso"));
const Pilares = lazyRetry(() => import("./pages/Pilares"));
const PilarDetail = lazyRetry(() => import("./pages/PilarDetail"));
const Desafio90 = lazyRetry(() => import("./pages/Desafio90"));
const MeusDesafios = lazyRetry(() => import("./pages/MeusDesafios"));
const Perfil = lazyRetry(() => import("./pages/Perfil"));
const Grupos = lazyRetry(() => import("./pages/Grupos"));
const GrupoDetail = lazyRetry(() => import("./pages/GrupoDetail"));
const AcceptInvite = lazyRetry(() => import("./pages/AcceptInvite"));
const DiscoverGroups = lazyRetry(() => import("./pages/DiscoverGroups"));
const Admin = lazyRetry(() => import("./pages/Admin"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Carregando...</p>
    </div>
  </div>
);

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  // ✅ FASE 10: Initialize performance monitoring
  usePerformanceMonitor();
  
  // Initialize background processes for authenticated users
  useMovingAverages();
  useAutoAdjust();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/auth/login" replace />;
}

const App = () => {
  // ✅ Inicializar monitoramento global de erros
  useEffect(() => {
    initGlobalErrorMonitoring();
  }, []);
  
  // ✅ Detectar erros de chunk e forçar reload
  useEffect(() => {
    const RELOAD_KEY = 'chunk_error_reload_count';
    const MAX_RELOADS = 3;
    
    const handleChunkError = async (event: ErrorEvent) => {
      const error = event.error || event.message;
      const errorMsg = typeof error === 'string' ? error : error?.message || '';
      
      // Detectar erros de chunk loading
      if (
        errorMsg.includes('Failed to fetch dynamically imported module') ||
        errorMsg.includes('Importing a module script failed') ||
        errorMsg.includes('error loading dynamically imported module')
      ) {
        console.error('🔴 Erro de chunk detectado:', errorMsg);
        
        // Reportar erro para monitoramento
        const reloadCount = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0');
        await reportChunkError(errorMsg, {
          reload_attempt: reloadCount + 1,
          max_reloads: MAX_RELOADS,
          filename: event.filename,
          url: window.location.href
        });
        
        // Prevenir loop infinito de reloads
        if (reloadCount < MAX_RELOADS) {
          sessionStorage.setItem(RELOAD_KEY, String(reloadCount + 1));
          console.log('🔄 Limpando cache e recarregando... (tentativa ' + (reloadCount + 1) + ')');
          await clearAllCaches();
          window.location.reload();
        } else {
          console.error('❌ Máximo de reloads atingido. Cache pode estar corrompido.');
          sessionStorage.removeItem(RELOAD_KEY);
          alert('Erro ao carregar a aplicação. Por favor, limpe o cache do navegador e tente novamente.');
        }
      }
    };
    
    // Limpar contador de reloads em carregamento bem-sucedido
    const resetReloadCounter = () => {
      sessionStorage.removeItem(RELOAD_KEY);
    };
    
    window.addEventListener('error', handleChunkError);
    window.addEventListener('load', resetReloadCounter);
    
    return () => {
      window.removeEventListener('error', handleChunkError);
      window.removeEventListener('load', resetReloadCounter);
    };
  }, []);
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <I18nProvider>
            <AuthInitializer />
            <DataPrefetchProvider>
              <PrefetchLoadingIndicator />
              <PerformanceMonitor />
              <CacheMonitor />
              <InstallPrompt />
              <PWAUpdatePrompt />
              <Toaster />
              <Sonner />
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
          <Route path="/" element={<Landing />} />
          
          {/* Auth Routes */}
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/signup" element={<Signup />} />
          
          {/* Onboarding Routes */}
          <Route
            path="/onboarding/incomplete"
            element={
              <ProtectedRoute>
                <OnboardingIncomplete />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/intro"
            element={
              <ProtectedRoute>
                <OnboardingIntro />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/roda"
            element={
              <ProtectedRoute>
                <RodaDaVida />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/identidade"
            element={
              <ProtectedRoute>
                <IdentidadeAspiracional />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/rotina"
            element={
              <ProtectedRoute>
                <Rotina />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/dados"
            element={
              <ProtectedRoute>
                <DadosPessoais />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/compromisso"
            element={
              <ProtectedRoute>
                <Compromisso />
              </ProtectedRoute>
            }
          />
          
          {/* App Routes */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pilares"
            element={
              <ProtectedRoute>
                <Pilares />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pilar/:slug"
            element={
              <ProtectedRoute>
                <PilarDetail />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/selecionar-pilares" 
            element={
              <ProtectedRoute>
                <EscolhaPilares standalone />
              </ProtectedRoute>
            } 
          />
          <Route path="/grupos" element={<ProtectedRoute><Grupos /></ProtectedRoute>} />
          <Route path="/grupo/:id" element={<ProtectedRoute><GrupoDetail /></ProtectedRoute>} />
          <Route path="/convite/:code" element={<ProtectedRoute><AcceptInvite /></ProtectedRoute>} />
          <Route path="/descobrir" element={<ProtectedRoute><DiscoverGroups /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/desafio" element={<ProtectedRoute><Desafio90 /></ProtectedRoute>} />
          <Route path="/desafio/:id" element={<ProtectedRoute><Desafio90 /></ProtectedRoute>} />
          <Route path="/meus-desafios" element={<ProtectedRoute><MeusDesafios /></ProtectedRoute>} />
          <Route path="/perfil" element={<ProtectedRoute><Perfil /></ProtectedRoute>} />
          
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
              </Suspense>
            </BrowserRouter>
            </DataPrefetchProvider>
          </I18nProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
